/**
 * Typed hooks + types for the folders API.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useApi } from './client.js';

export type FolderType = 'reports' | 'dashboards' | 'frameworks' | 'mixed' | 'presentations';

export interface Folder {
  id: string;
  parentId: string | null;
  name: string;
  type: FolderType;
  createdAt: string;
}

export interface FolderTreeNode extends Folder {
  children: FolderTreeNode[];
}

export function useFolders(type?: FolderType, options?: { enabled?: boolean }) {
  const api = useApi();
  const qs = type ? `?type=${type}` : '';
  return useQuery<Folder[]>({
    queryKey: ['folders', type ?? 'all'],
    queryFn: () => api.fetch(`/folders${qs}`),
    enabled: options?.enabled ?? true,
  });
}

export function useFolderTree(type?: FolderType) {
  const api = useApi();
  const qs = `?shape=tree${type ? `&type=${type}` : ''}`;
  return useQuery<FolderTreeNode[]>({
    queryKey: ['folders', 'tree', type ?? 'all'],
    queryFn: () => api.fetch(`/folders${qs}`),
  });
}

/** Invalidate every folders + report-templates cache on success — the
 *  sidebar is derived from both and we'd rather over-refresh than show
 *  a stale tree. Cheap at our list sizes. */
function invalidateFoldersAndTemplates(qc: ReturnType<typeof useQueryClient>): void {
  void qc.invalidateQueries({ queryKey: ['folders'] });
  void qc.invalidateQueries({ queryKey: ['report-templates'] });
  void qc.invalidateQueries({ queryKey: ['reports'] });
}

export function useCreateFolder() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { name: string; type: FolderType; parentId?: string | null }) =>
      api.fetch<Folder>('/folders', {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    onSuccess: () => invalidateFoldersAndTemplates(qc),
  });
}

export function useRenameFolder() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) =>
      api.fetch<Folder>(`/folders/${id}`, {
        method: 'PATCH',
        body: JSON.stringify({ name }),
      }),
    onSuccess: () => invalidateFoldersAndTemplates(qc),
  });
}

export function useMoveFolder() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, parentId }: { id: string; parentId: string | null }) =>
      api.fetch<Folder>(`/folders/${id}`, {
        method: 'PATCH',
        body: JSON.stringify({ parentId }),
      }),
    onSuccess: () => invalidateFoldersAndTemplates(qc),
  });
}

export function useDeleteFolder() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.fetch<{ ok: true }>(`/folders/${id}`, { method: 'DELETE' }),
    onSuccess: () => invalidateFoldersAndTemplates(qc),
  });
}
